import React, { useState } from "react";
import { PlusCircle, Save, Eye, Award, CheckCircle } from "lucide-react";
import { useAwardsSettings } from "../../../hooks/useAwardsSettings";
import { AwardTierCard } from "../../../components/judging/AwardTierCard";
import { AwardPreview } from "../../../components/judging/AwardPreview";
import { ModeToggle } from "../../../components/ui/ModeToggle";
import { CardHeader } from "../../../components/ui/CardHeader";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { ErrorAlert } from "../../../components/ui/ErrorAlert";


/**
 * Settings component for managing award tiers
 */
export function AwardsSettings() {
  
  const {
    awards,
    isPercentageBased,
    maxPointsPerJudge,
    numberOfJudges,
    totalPossiblePoints,
    loading,
    error,
    hasChanges,
    successMessage,
    addAward,
    updateAward,
    removeAward,
    saveAwards,
    toggleScoringMode,
    updateMaxPointsPerJudge,
    updateNumberOfJudges
  } = useAwardsSettings();

  if (loading && awards.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Awards Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Award Settings */}
          <div className="card bg-base-100 border border-base-300">
            <div className="card-body p-6">
              <CardHeader
                icon={<Award />}
                title="Award Tiers"
                description={
                  isPercentageBased
                    ? "Configure percentage-based award thresholds"
                    : "Configure point-based award thresholds"
                }
              />

              <div className="flex justify-between items-center mb-4">
                <ModeToggle
                  isPercentageMode={isPercentageBased}
                  onChange={toggleScoringMode}
                />
              </div>

              {/* Scoring Configuration */}
              <div className="bg-base-200 p-4 rounded-lg mt-4 space-y-4">
                <h3 className="font-semibold mb-2">Scoring Configuration</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Points per Judge</label>
                    <input
                      type="number"
                      value={maxPointsPerJudge}
                      onChange={(e) => updateMaxPointsPerJudge(parseInt(e.target.value))}
                      className="input input-bordered w-full"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="label">Number of Judges</label>
                    <input
                      type="number"
                      value={numberOfJudges}
                      onChange={(e) => updateNumberOfJudges(parseInt(e.target.value))}
                      className="input input-bordered w-full"
                      min="1"
                    />
                  </div>
                </div>
                <div className="text-sm opacity-70">
                  Total possible points: {totalPossiblePoints}
                </div>
              </div>

              <ErrorAlert 
                message={error}
                className="mt-4"
              />

              {successMessage && (
                <div className="alert alert-success mt-4">
                  <CheckCircle className="w-5 h-5" />
                  <span>{successMessage}</span>
                </div>
              )}

              <div className="divider">Award Thresholds</div>

              {/* Awards List */}
              <div className="space-y-4">
                {awards.map((award) => (
                  <AwardTierCard
                    key={award.id}
                    award={award}
                    isPercentageBased={isPercentageBased}
                    maxPossibleValue={totalPossiblePoints}
                    onChange={updateAward}
                    onRemove={removeAward}
                  />
                ))}

                <button
                  onClick={addAward}
                  className="btn btn-outline btn-block btn-sm"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add Award Tier
                </button>
              </div>

              <div className="card-actions justify-end mt-6">
                <button
                  onClick={saveAwards}
                  disabled={!hasChanges || awards.length === 0}
                  className="btn btn-primary"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Awards
                </button>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="card bg-base-100 border border-base-300">
            <div className="card-body p-6">
              <CardHeader
                icon={<Eye />}
                title="Awards Preview"
                description="See how your awards will appear to participants"
              />

              <div className="space-y-4">
                {awards.length === 0 ? (
                  <div className="text-center py-8 text-base-content/60">
                    Add award tiers to see preview
                  </div>
                ) : (
                  <div className="space-y-2">
                    {[...awards]
                      .sort((a, b) => b.maxScore - a.maxScore)
                      .map((award) => (
                        <AwardPreview
                          key={award.id}
                          award={award}
                          isPercentageBased={isPercentageBased}
                        />
                      ))}
                  </div>
                )}
              </div>

              {/* Points Breakdown */}
              {!isPercentageBased && awards.length > 0 && (
                <div className="mt-6 p-4 bg-base-200 rounded-lg">
                  <h4 className="font-semibold mb-2">Points Breakdown</h4>
                  <div className="text-sm space-y-1">
                    <div>Total Possible: {totalPossiblePoints} points</div>
                    <div>{numberOfJudges} judges × {maxPointsPerJudge} points each</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
    </div>
  );
}